import { z } from 'zod';
import {
  ApiPath,
  periodeHonorSchema,
  slipHonorariumSchema,
  totalJpMengajarSchema,
  draftHonorSchema,
  generateDraftHonorResultSchema,
  type CreatePeriodeHonorRequest,
  type UpdatePeriodeHonorRequest,
  type StatusPeriodeHonor,
  type SlipHonorariumListQuery,
  type CreateTotalJpMengajarRequest,
  type UpdateTotalJpMengajarRequest,
  type TotalJpMengajarListQuery,
} from '@app/shared';
import { apiGet, apiPost, apiPatch, apiDelete, apiPut, apiList } from '../../lib/api';

// --- Periode Honor (Milestone 4.1) ---

export async function getPeriodeHonorList(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: StatusPeriodeHonor;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}) {
  return apiList(ApiPath.HONORARIUM.PERIODE, periodeHonorSchema, params);
}

export async function getPeriodeHonor(id: number) {
  return apiGet(`${ApiPath.HONORARIUM.PERIODE}/${id}`, periodeHonorSchema);
}

// Alias untuk compatibility
export const getPeriodeHonorDetail = getPeriodeHonor;

export async function createPeriodeHonor(body: CreatePeriodeHonorRequest) {
  return apiPost(ApiPath.HONORARIUM.PERIODE, body, periodeHonorSchema);
}

export async function updatePeriodeHonor(id: number, body: UpdatePeriodeHonorRequest) {
  return apiPatch(`${ApiPath.HONORARIUM.PERIODE}/${id}`, body, periodeHonorSchema);
}

export async function deletePeriodeHonor(id: number) {
  return apiDelete(`${ApiPath.HONORARIUM.PERIODE}/${id}`);
}

export async function lockPeriodeHonor(id: number) {
  return apiPost(`${ApiPath.HONORARIUM.PERIODE}/${id}/lock`, {}, periodeHonorSchema);
}

export async function unlockPeriodeHonor(id: number) {
  return apiPost(`${ApiPath.HONORARIUM.PERIODE}/${id}/unlock`, {}, periodeHonorSchema);
}

// --- Slip Honorarium (Milestone 4.3) ---

type SlipQueryValue = string | number | boolean | undefined | null;
type SlipQueryParams = Record<string, SlipQueryValue | SlipQueryValue[]>;

export async function getSlipHonorariumList(params?: Partial<SlipHonorariumListQuery>) {
  return apiList(ApiPath.HONORARIUM.SLIP, slipHonorariumSchema, params as SlipQueryParams);
}

export async function getSlipHonorarium(pegawaiId: number, periodeId: number) {
  return apiGet(`${ApiPath.HONORARIUM.SLIP}/${pegawaiId}`, slipHonorariumSchema, {
    periode_id: periodeId,
  });
}

// --- Draft Honor (Milestone 4.4) ---

export async function generateDraftHonor(periodeHonorId: number) {
  return apiPost(
    `${ApiPath.HONORARIUM.PERIODE}/${periodeHonorId}/generate`,
    {},
    generateDraftHonorResultSchema,
  );
}

export async function getDraftHonorList(periodeHonorId: number) {
  const response = await apiGet(
    `${ApiPath.HONORARIUM.PERIODE}/${periodeHonorId}/draft`,
    draftHonorSchema.array(),
  );
  return response;
}

export async function deleteDraftHonor(periodeHonorId: number) {
  return apiDelete(`${ApiPath.HONORARIUM.PERIODE}/${periodeHonorId}/draft`);
}

// --- Update Total JP pada Draft Honor (Milestone 5) ---

export async function updateDraftHonorTotalJp(
  periodeHonorId: number,
  pegawaiId: number,
  totalJp: number,
) {
  return apiPut(
    `${ApiPath.HONORARIUM.PERIODE}/${periodeHonorId}/pegawai/${pegawaiId}/total-jp`,
    { total_jp: totalJp },
    draftHonorSchema,
  );
}

// --- Total JP Mengajar (Milestone 4.5) ---

export async function getTotalJpMengajarList(params?: TotalJpMengajarListQuery) {
  return apiList(ApiPath.HONORARIUM.TOTAL_JP_MENGAJAR, totalJpMengajarSchema, params);
}

export async function getTotalJpMengajar(id: number) {
  return apiGet(`${ApiPath.HONORARIUM.TOTAL_JP_MENGAJAR}/${id}`, totalJpMengajarSchema);
}

export async function createTotalJpMengajar(body: CreateTotalJpMengajarRequest) {
  return apiPost(ApiPath.HONORARIUM.TOTAL_JP_MENGAJAR, body, totalJpMengajarSchema);
}

export async function updateTotalJpMengajar(id: number, body: UpdateTotalJpMengajarRequest) {
  return apiPatch(`${ApiPath.HONORARIUM.TOTAL_JP_MENGAJAR}/${id}`, body, totalJpMengajarSchema);
}

export async function deleteTotalJpMengajar(id: number) {
  return apiDelete(`${ApiPath.HONORARIUM.TOTAL_JP_MENGAJAR}/${id}`);
}

// --- Laporan Honorarium (Milestone 5.3) ---

export type LaporanHonorariumRow = {
  pegawai_id: number;
  nip_nik: string;
  nama: string;
  sekolah_id: number;
  sekolah_nama: string;
  jabatan_nama: string;
  kode_periode: string;
  status_slip: 'draft' | 'final';
  total_jp: number;
  honor_jp: number;
  hari_hadir: number;
  honor_kehadiran: number;
  honor_jabatan: number;
  honor_wali_kelas: number;
  total_insentif: number;
  total_honor: number;
};

export type LaporanHonorariumGrandTotal = {
  honor_jp: number;
  honor_kehadiran: number;
  honor_jabatan: number;
  honor_wali_kelas: number;
  total_insentif: number;
  total_honor: number;
  jumlah_pegawai: number;
};

export type LaporanHonorariumResult = {
  rows: LaporanHonorariumRow[];
  total: number;
  page: number;
  limit: number;
  grand_total: LaporanHonorariumGrandTotal;
};

export async function getLaporanHonorarium(params: {
  periode_honor_id: number;
  sekolah_id?: number;
  pegawai_id?: number;
  search?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}): Promise<LaporanHonorariumResult> {
  const res = await apiGet(
    ApiPath.HONORARIUM.LAPORAN,
    z.object({
      rows: z.array(
        z.object({
          pegawai_id: z.number(),
          nip_nik: z.string(),
          nama: z.string(),
          sekolah_id: z.number(),
          sekolah_nama: z.string(),
          jabatan_nama: z.string(),
          kode_periode: z.string(),
          status_slip: z.enum(['draft', 'final']),
          total_jp: z.number(),
          honor_jp: z.number(),
          hari_hadir: z.number(),
          honor_kehadiran: z.number(),
          honor_jabatan: z.number(),
          honor_wali_kelas: z.number(),
          total_insentif: z.number(),
          total_honor: z.number(),
        }),
      ),
      total: z.number(),
      page: z.number(),
      limit: z.number(),
      grand_total: z.object({
        honor_jp: z.number(),
        honor_kehadiran: z.number(),
        honor_jabatan: z.number(),
        honor_wali_kelas: z.number(),
        total_insentif: z.number(),
        total_honor: z.number(),
        jumlah_pegawai: z.number(),
      }),
    }),
    params as Record<string, string | number | undefined>,
  );
  return res as LaporanHonorariumResult;
}

/**
 * Download CSV laporan honorarium (streaming, formula injection safe).
 * Triggers browser download.
 */
export async function downloadLaporanHonorariumCsv(params: {
  periode_honor_id: number;
  sekolah_id?: number;
  pegawai_id?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}): Promise<void> {
  const API_BASE = '/api/v1';
  const search = new URLSearchParams();
  search.set('periode_honor_id', String(params.periode_honor_id));
  if (params.sekolah_id) search.set('sekolah_id', String(params.sekolah_id));
  if (params.pegawai_id) search.set('pegawai_id', String(params.pegawai_id));
  if (params.search) search.set('search', params.search);
  if (params.sort_by) search.set('sort_by', params.sort_by);
  if (params.sort_dir) search.set('sort_dir', params.sort_dir);

  const url = `${API_BASE}${ApiPath.HONORARIUM.LAPORAN_CSV}?${search.toString()}`;
  const res = await fetch(url, { method: 'GET', credentials: 'include' });

  if (!res.ok) {
    throw new Error(`Gagal mengunduh CSV (HTTP ${res.status})`);
  }

  const blob = await res.blob();
  const contentDisposition = res.headers.get('Content-Disposition');
  let filename = `laporan-honorarium-${params.periode_honor_id}.csv`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) filename = match[1];
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
}
